import { msg } from '@lingui/core/macro';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useMemo, useState } from 'react';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { useCopyBoardTemplateMutation } from 'modules/template-editor/store/board-templates-api';
import { useActiveBoardTemplate } from 'modules/template-editor/ui/hooks/use-active-board-template';
import { useOwnerBoardTemplates } from 'modules/template-editor/ui/hooks/use-owner-board-templates';

const PRESET: TemplatePresetsNameList = 'duplicate';

function useDuplicateModal() {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { info } = useOrganizer();
  const { templates } = useOwnerBoardTemplates();
  const activeTemplate = useActiveBoardTemplate();
  const [copyTemplate, { isLoading }] = useCopyBoardTemplateMutation();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!activeTemplate) return;
      if (!info) return;
      const { width, height, name } = values;
      const resolution = { width: +width, height: +height };
      try {
        await copyTemplate({
          templateId: activeTemplate.id,
          ownerId: info.id,
          resolution,
          name: name.toString().trim(),
        }).unwrap();
        close();
      } catch (error) {
        toastApiError(error, msg`Не удалось создать копию`);
      }
    },
    [activeTemplate, close, copyTemplate, info, toastApiError],
  );

  const initialValue = useMemo(
    () => ({
      // eslint-disable-next-line lingui/no-unlocalized-strings -- имя копии уходит в базу, это данные, а не подпись интерфейса
      name: activeTemplate?.name ? activeTemplate?.name + ' (дубликат)' : '',
      width: activeTemplate?.resolution.width?.toString() ?? '',
      height: activeTemplate?.resolution.height?.toString() ?? '',
      visibility: getVisibilityOptions().find((option) => option.value === activeTemplate?.visibility) ?? getVisibilityOptions()[0],
      access: getAccessOptions().find((option) => option.value === activeTemplate?.access) ?? getAccessOptions()[0],
    }),
    [activeTemplate],
  );

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isLoading}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useDuplicateModal;
